import { t } from "../../i18n";
import { Link, useSearchParams } from "react-router-dom";
import { GiteaPage } from "./GiteaPage";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import type { Integration, IntegrationKind, User } from "@lumen/shared";
import {
  ArrowLeft,
  Check,
  Copy,
  KeyRound,
  Link2,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { api } from "../../api";
import { completePersonalizationRequestFromLocation } from "../../personalization";
import { SettingsPage } from "../settings/ProfilePage";
import { CapabilityPills } from "../../components/CapabilityPills";
import { PickerCard } from "../../components/PickerCard";
import { FieldInput } from "../../components/composer/FieldInput";

import {
  businessIntegrationKinds,
  capabilityLabels,
  catalog,
  definitionFor,
  draftForIntegration,
  emptyDraft,
  groups,
  groupLabels,
  iconFor,
  type ActionApproval,
  type Draft,
  textField,
} from "./IntegrationsPage.utils";

export function IntegrationsPage({
  user,
  organizationId,
  siteOrganizationManagement = false,
  organizationOnly = false,
}: {
  user: User;
  organizationId?: string;
  siteOrganizationManagement?: boolean;
  organizationOnly?: boolean;
}) {
  const [items, setItems] = useState<Integration[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const giteaId = searchParams.get("gitea");
  const [approvals, setApprovals] = useState<ActionApproval[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [editing, setEditing] = useState<Integration | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [copiedWebhookId, setCopiedWebhookId] = useState<string | null>(null);
  const organizationScope = siteOrganizationManagement || organizationOnly;
  const contextSuffix = organizationId
    ? `?organizationId=${encodeURIComponent(organizationId)}`
    : "";
  const selected = useMemo(
    () => (draft ? definitionFor(draft.kind) : undefined),
    [draft],
  );

  const load = useCallback(
    () =>
      Promise.all([
        api<Integration[]>(`/integrations${contextSuffix}`),
        organizationId
          ? Promise.resolve<ActionApproval[]>([])
          : api<ActionApproval[]>("/integration-action-approvals"),
      ]).then(([nextItems, nextApprovals]) => {
        setItems(
          organizationOnly
            ? nextItems.filter((item) => item.scope === "organization")
            : nextItems,
        );
        setApprovals(nextApprovals);
      }),
    [contextSuffix, organizationId, organizationOnly],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setDraft(null);
    setPickerOpen(true);
    setSearch("");
    setError("");
    setMessage("");
  };
  const chooseKind = (kind: IntegrationKind) => {
    setEditing(null);
    setDraft({
      ...emptyDraft(kind),
      ...(organizationScope ? { scope: "organization" as const } : {}),
    });
    setPickerOpen(false);
    setError("");
    setMessage("");
  };
  const setConfig = (key: string, value: string | boolean) =>
    setDraft((current) =>
      current
        ? { ...current, config: { ...current.config, [key]: value } }
        : current,
    );
  const closeForm = () => {
    setDraft(null);
    setEditing(null);
    setPickerOpen(false);
  };
  const testConnection = async (item: Pick<Integration, "id" | "name">) => {
    const result = await api<{ ok: boolean; message: string }>(
      `/integrations/${item.id}/test${contextSuffix}`,
      { method: "POST" },
    );
    if (!result.ok) throw new Error(result.message);
    return result.message;
  };
  const decideApproval = async (
    approval: ActionApproval,
    decision: "approved" | "denied",
  ) => {
    try {
      await api(`/integration-action-approvals/${approval.id}/decision`, {
        method: "POST",
        body: JSON.stringify({ decision }),
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not decide approval",
      );
    }
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const config =
        draft.kind === "mailbox"
          ? {
              ...draft.config,
              ...(draft.config.imapPort !== undefined
                ? { imapPort: Number(draft.config.imapPort || 993) }
                : {}),
              ...(draft.config.smtpPort !== undefined
                ? { smtpPort: Number(draft.config.smtpPort || 587) }
                : {}),
            }
          : Object.fromEntries(
              Object.entries(draft.config).filter(([, value]) => value !== ""),
            );
      const saved = await api<Integration>(
        `${editing ? `/integrations/${editing.id}` : "/integrations"}${contextSuffix}`,
        {
          method: editing ? "PATCH" : "POST",
          body: JSON.stringify({
            name: draft.name,
            scope: draft.scope,
            kind: draft.kind,
            config,
          }),
        },
      );
      closeForm();
      await load();
      try {
        const testMessage = await testConnection(saved);
        if (!editing)
          await completePersonalizationRequestFromLocation({
            resourceType: "integration",
            resourceId: saved.id,
          }).catch(() => undefined);
        setMessage(`${saved.name}: ${testMessage}`);
        await load();
      } catch (reason) {
        setError(
          `${saved.name} was saved, but the connection test needs attention: ${reason instanceof Error ? reason.message : "the provider did not respond successfully"}`,
        );
      }
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save integration",
      );
    } finally {
      setBusy(false);
    }
  };
  const test = async (item: Integration) => {
    setMessage("");
    setError("");
    try {
      const result = await testConnection(item);
      setMessage(`${item.name}: ${result}`);
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Connection test failed",
      );
      await load().catch(() => undefined);
    }
  };
  const copyWebhook = async (item: Integration) => {
    if (!item.webhookPath) return;
    const path = item.webhookPath.replace("{id}", item.id);
    const url = new URL(path, window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedWebhookId(item.id);
      window.setTimeout(() => setCopiedWebhookId(null), 1800);
    } catch {
      setError("Could not copy the webhook URL. Select it manually instead.");
    }
  };
  const toggle = async (item: Integration) => {
    try {
      await api(`/integrations/${item.id}${contextSuffix}`, {
        method: "PATCH",
        body: JSON.stringify({ enabled: !item.enabled }),
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not update integration",
      );
    }
  };
  const remove = async (item: Integration) => {
    if (!window.confirm(`Remove ${item.name}?`)) return;
    try {
      await api(`/integrations/${item.id}${contextSuffix}`, {
        method: "DELETE",
      });
      await completePersonalizationRequestFromLocation({
        resourceType: "integration",
        resourceId: item.id,
      }).catch(() => undefined);
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not remove integration",
      );
    }
  };

  const filteredCatalog = catalog.filter((item) =>
    `${item.label} ${item.description} ${item.group}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const standardFields = [
    ...(selected?.fields.filter((field) => !field.advanced) ?? []),
    ...(selected && businessIntegrationKinds.has(selected.kind)
      ? [
          textField(
            "webhookSecret",
            "Lumen webhook secret",
            "Optional HMAC secret for provider webhooks sent to the copied Lumen URL.",
            {
              type: "password",
              secret: true,
              placeholder: "Paste webhook secret",
            },
          ),
        ]
      : []),
  ];
  const advancedFields = [
    ...(selected?.fields.filter((field) => field.advanced) ?? []),
    ...(selected && businessIntegrationKinds.has(selected.kind)
      ? [
          textField(
            "refreshToken",
            "OAuth refresh token",
            "Optional refresh token for providers that support OAuth rotation.",
            { type: "password", secret: true, advanced: true },
          ),
          textField(
            "clientId",
            "OAuth client ID",
            "Optional OAuth client identifier.",
            { advanced: true },
          ),
          textField(
            "clientSecret",
            "OAuth client secret",
            "Optional OAuth client secret.",
            { type: "password", secret: true, advanced: true },
          ),
          textField(
            "tokenEndpoint",
            "OAuth token endpoint",
            "Optional HTTPS token endpoint used to refresh access tokens.",
            { type: "url", advanced: true },
          ),
          textField(
            "accessTokenExpiresAt",
            "Access token expiry",
            "Optional ISO timestamp; refresh begins one minute before expiry.",
            { advanced: true, placeholder: "2026-08-01T12:00:00Z" },
          ),
        ]
      : []),
  ];

  if (giteaId)
    return (
      <GiteaPage
        key={`${organizationId ?? ""}:${giteaId}`}
        integrationId={giteaId}
        name={items.find((item) => item.id === giteaId)?.name ?? "Gitea"}
        organizationId={organizationId}
        onBack={() => {
          const next = new URLSearchParams(searchParams);
          next.delete("gitea");
          setSearchParams(next);
        }}
      />
    );

  return (
    <SettingsPage
      title={t("Integrations")}
      description="Connect the tools your automations can use. Each connection shows exactly what it unlocks and what you need to get started."
      action={
        !draft && !pickerOpen ? (
          <button className="button-primary" onClick={openNew}>
            <Plus size={16} /> {t("Connect an integration")}
          </button>
        ) : undefined
      }
    >
      {(error || message) && (
        <p
          className={`mb-4 text-sm whitespace-pre-line ${error ? "text-red-600" : "text-emerald-600"}`}
        >
          {error || message}
        </p>
      )}
      {approvals.some((approval) => approval.status === "pending") && (
        <section className="panel mb-6 border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
          <div className="flex items-center gap-2">
            <ShieldCheck size={17} className="text-amber-700" />
            <h3 className="font-bold">
              {t("Pending business action approvals")}
            </h3>
          </div>
          <div className="mt-3 space-y-2">
            {approvals
              .filter((approval) => approval.status === "pending")
              .map((approval) => (
                <div
                  key={approval.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-white/70 p-3 dark:border-amber-900/40 dark:bg-black/10"
                >
                  <div>
                    <p className="text-sm font-semibold">
                      {items.find((item) => item.id === approval.integrationId)
                        ?.name ?? "Business integration"}{" "}
                      · {approval.action}
                    </p>
                    <p className="text-xs text-stone-500">
                      {t("Expires {{date}}", {
                        date: new Date(approval.expiresAt).toLocaleString(),
                      })}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="button"
                      onClick={() => void decideApproval(approval, "denied")}
                    >
                      {t("Deny")}
                    </button>
                    <button
                      className="button-primary"
                      onClick={() => void decideApproval(approval, "approved")}
                    >
                      {t("Approve")}
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </section>
      )}

      {pickerOpen && (
        <section className="mb-6">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <button
                type="button"
                className="button mb-3 -ml-3 text-stone-500"
                onClick={closeForm}
              >
                <ArrowLeft size={15} /> {t("Back to connections")}
              </button>
              <h3 className="text-2xl font-bold">
                {t("What would you like to connect?")}
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-stone-500">
                {t(
                  "Choose a service by the job you want it to do. You can connect\n                more than one integration.",
                )}
              </p>
            </div>
            <button type="button" className="button" onClick={closeForm}>
              <X size={16} /> {t("Close")}
            </button>
          </div>
          <label className="relative mb-5 block max-w-xl">
            <Search
              className="absolute top-1/2 left-3 -translate-y-1/2 text-stone-400"
              size={17}
            />
            <span className="sr-only">{t("Search integrations")}</span>
            <input
              className="field pl-10"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("Search by service or what it does")}
            />
          </label>
          <div className="space-y-7">
            {groups.map((group) => {
              const groupItems = filteredCatalog.filter(
                (item) => item.group === group,
              );
              if (!groupItems.length) return null;
              return (
                <div key={group}>
                  <div className="mb-3 flex items-center gap-2">
                    <h4 className="text-sm font-bold tracking-wider text-stone-500 uppercase">
                      {groupLabels[group] ?? group}
                    </h4>
                    <span className="h-px flex-1 bg-stone-200 dark:bg-white/10" />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {groupItems.map((item) => (
                      <PickerCard
                        key={item.kind}
                        definition={item}
                        onChoose={() => chooseKind(item.kind)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {!filteredCatalog.length && (
              <div className="panel py-10 text-center text-stone-500">
                {t("No integrations match “{{search}}”.", { search })}
              </div>
            )}
          </div>
        </section>
      )}

      {draft && selected && (
        <form className="panel mb-6 overflow-hidden" onSubmit={save}>
          <div className="border-b border-stone-200 bg-stone-50/70 p-5 sm:p-6 dark:border-white/10 dark:bg-white/3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex gap-3">
                <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-lumen-100 text-lumen-700 dark:bg-lumen-950/60 dark:text-lumen-300">
                  {iconFor(selected.kind, 22)}
                </span>
                <div>
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="text-xs font-bold tracking-wider text-lumen-700 uppercase dark:text-lumen-300">
                      {editing ? "Edit connection" : "New connection"}
                    </span>
                    <span className="text-xs text-stone-400">·</span>
                    <span className="text-xs text-stone-500">
                      {selected.group}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold">{selected.label}</h3>
                  <p className="mt-1 max-w-2xl text-sm text-stone-500">
                    {selected.description}
                  </p>
                </div>
              </div>
              <button type="button" className="button" onClick={closeForm}>
                <X size={16} /> {t("Close")}
              </button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-xs font-semibold text-stone-500">
                {t("This connection enables")}
              </span>
              <CapabilityPills capabilities={selected.capabilities} />
            </div>
          </div>

          <div className="space-y-7 p-5 sm:p-6">
            <section className="rounded-2xl border border-lumen-100 bg-lumen-50/60 p-4 dark:border-lumen-900/40 dark:bg-lumen-950/20">
              <div className="flex gap-3">
                <ShieldCheck
                  className="mt-0.5 shrink-0 text-lumen-700 dark:text-lumen-300"
                  size={18}
                />
                <div>
                  <h4 className="font-bold text-lumen-950 dark:text-lumen-100">
                    {t("Before you start")}
                  </h4>
                  <p className="mt-1 text-sm leading-6 text-lumen-900/75 dark:text-lumen-100/75">
                    {selected.setup}
                  </p>
                  {editing && (
                    <p className="mt-2 text-xs font-medium text-lumen-900/75 dark:text-lumen-100/75">
                      {t(
                        "Your saved credentials are hidden. Leave a credential\n                      blank to keep it unchanged.",
                      )}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <section>
              <h4 className="mb-1 font-bold">{t("Name and access")}</h4>
              <p className="mb-4 text-sm text-stone-500">
                {t(
                  "Give this connection a name you’ll recognize in automations,\n                then choose who can use it.",
                )}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <label>
                  <span className="label">{t("Connection name")}</span>
                  <input
                    className="field"
                    required
                    value={draft.name}
                    onChange={(event) =>
                      setDraft({ ...draft, name: event.target.value })
                    }
                    placeholder={`e.g. ${selected.label}`}
                  />
                  <span className="mt-1 block text-xs text-stone-500">
                    {t(
                      "This is the name shown when you select the connection\n                    elsewhere.",
                    )}
                  </span>
                </label>
                <div>
                  <span className="label">{t("Who can use this?")}</span>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {!organizationScope && (
                      <label
                        className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition ${draft.scope === "personal" ? "border-lumen-500 bg-lumen-50/60 dark:bg-lumen-950/30" : "border-stone-200 hover:border-stone-300 dark:border-white/10 dark:hover:border-white/20"}`}
                      >
                        <input
                          className="mt-1 accent-lumen-600"
                          type="radio"
                          name="integration-scope"
                          checked={draft.scope === "personal"}
                          onChange={() =>
                            setDraft({ ...draft, scope: "personal" })
                          }
                        />
                        <span>
                          <span className="block text-sm font-semibold">
                            {t("Only me")}
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-stone-500">
                            {t("Use this connection in your automations.")}
                          </span>
                        </span>
                      </label>
                    )}
                    {(user.organizationPermissions.includes(
                      "integrations:manage",
                    ) ||
                      siteOrganizationManagement ||
                      organizationOnly ||
                      editing?.scope === "organization") && (
                      <label
                        className={`flex gap-3 rounded-xl border p-3 transition ${editing?.scope === "organization" && !user.organizationPermissions.includes("integrations:manage") ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${draft.scope === "organization" ? "border-lumen-500 bg-lumen-50/60 dark:bg-lumen-950/30" : "border-stone-200 hover:border-stone-300 dark:border-white/10 dark:hover:border-white/20"}`}
                      >
                        <input
                          className="mt-1 accent-lumen-600"
                          type="radio"
                          name="integration-scope"
                          checked={draft.scope === "organization"}
                          disabled={
                            !organizationScope &&
                            editing?.scope === "organization" &&
                            !user.organizationPermissions.includes(
                              "integrations:manage",
                            )
                          }
                          onChange={() =>
                            setDraft({ ...draft, scope: "organization" })
                          }
                        />
                        <span>
                          <span className="block text-sm font-semibold">
                            {t("Everyone in this organization")}
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-stone-500">
                            {t("Share this connection with your team.")}
                          </span>
                        </span>
                      </label>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {draft.kind === "mailbox" ? (
              <>
                <section>
                  <h4 className="mb-1 font-bold">{t("Receive email")}</h4>
                  <p className="mb-4 text-sm text-stone-500">
                    {t(
                      "These details tell Lumen where to look for new messages.",
                    )}
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    {standardFields.slice(0, 4).map((field) => (
                      <FieldInput
                        key={field.key}
                        field={field}
                        draft={draft}
                        editing={Boolean(editing)}
                        setConfig={setConfig}
                      />
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm text-stone-600 dark:text-stone-300">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.config.imapSecure ?? true)}
                        onChange={(event) =>
                          setConfig("imapSecure", event.target.checked)
                        }
                      />{" "}
                      {t("Use a secure incoming connection (TLS)")}
                    </label>
                  </div>
                </section>
                <section>
                  <h4 className="mb-1 font-bold">{t("Send replies")}</h4>
                  <p className="mb-4 text-sm text-stone-500">
                    {t(
                      "These details are used when Lumen replies to an incoming\n                    email.",
                    )}
                  </p>
                  <div className="grid gap-4 md:grid-cols-2">
                    {standardFields.slice(4).map((field) => (
                      <FieldInput
                        key={field.key}
                        field={field}
                        draft={draft}
                        editing={Boolean(editing)}
                        setConfig={setConfig}
                      />
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-x-5 gap-y-3 text-sm text-stone-600 dark:text-stone-300">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.config.smtpSecure)}
                        onChange={(event) =>
                          setConfig("smtpSecure", event.target.checked)
                        }
                      />{" "}
                      {t("Use secure outgoing mail (TLS)")}
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={Boolean(draft.config.smtpRequireTls ?? true)}
                        onChange={(event) =>
                          setConfig("smtpRequireTls", event.target.checked)
                        }
                      />{" "}
                      {t("Require a secure connection (STARTTLS)")}
                    </label>
                  </div>
                </section>
              </>
            ) : (
              <section>
                <h4 className="mb-1 font-bold">{t("Connection details")}</h4>
                <p className="mb-4 text-sm text-stone-500">
                  {t(
                    "Use the labels and examples below to find each value in {{service}}.",
                    { service: selected.label },
                  )}
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  {standardFields.map((field) => (
                    <FieldInput
                      key={field.key}
                      field={field}
                      draft={draft}
                      editing={Boolean(editing)}
                      setConfig={setConfig}
                    />
                  ))}
                </div>
                {advancedFields.length > 0 && (
                  <details className="mt-5 rounded-xl border border-stone-200 px-4 py-3 dark:border-white/10">
                    <summary className="cursor-pointer text-sm font-semibold text-stone-700 dark:text-stone-200">
                      {t("Advanced connection settings")}
                    </summary>
                    <p className="mt-2 text-xs text-stone-500">
                      {t(
                        "These are pre-filled for the standard hosted service.\n                      Change them only if your organization uses a self-hosted\n                      provider.",
                      )}
                    </p>
                    <div className="mt-4 grid gap-4 md:grid-cols-2">
                      {advancedFields.map((field) => (
                        <FieldInput
                          key={field.key}
                          field={field}
                          draft={draft}
                          editing={Boolean(editing)}
                          setConfig={setConfig}
                        />
                      ))}
                    </div>
                  </details>
                )}
              </section>
            )}

            <div className="flex flex-col gap-3 border-t border-stone-200 pt-5 sm:flex-row sm:items-center sm:justify-between dark:border-white/10">
              <div className="flex items-start gap-2 text-xs leading-5 text-stone-500">
                <KeyRound className="mt-0.5 shrink-0" size={14} />{" "}
                {t(
                  "Secrets are\n                encrypted before storage. You can test the connection after\n                saving.",
                )}
              </div>
              <div className="flex gap-2">
                <button type="button" className="button" onClick={closeForm}>
                  {t("Cancel")}
                </button>
                <button className="button-primary" disabled={busy}>
                  {busy
                    ? "Saving and testing…"
                    : editing
                      ? "Save and test"
                      : "Save and test connection"}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {!pickerOpen && !draft && (
        <>
          {items.length > 0 && (
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-bold">{t("Connected services")}</h3>
              <span className="text-sm text-stone-500">
                {items.length}{" "}
                {items.length === 1 ? "connection" : "connections"}
              </span>
            </div>
          )}
          <div className="space-y-3">
            {items.map((item) => {
              const definition = definitionFor(item.kind);
              const capabilities =
                definition?.capabilities ??
                (item.capabilities ?? []).map((key) => ({
                  key,
                  label: capabilityLabels[key] ?? key,
                }));
              const webhookUrl = item.webhookPath
                ? new URL(
                    item.webhookPath.replace("{id}", item.id),
                    window.location.origin,
                  ).toString()
                : "";
              return (
                <article
                  key={item.id}
                  className="panel flex flex-wrap items-center gap-4 p-4 sm:p-5"
                >
                  <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-lumen-50 text-lumen-700 dark:bg-lumen-950/40 dark:text-lumen-300">
                    {iconFor(item.kind)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-bold">{item.name}</h3>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${item.lastError ? "bg-red-50 text-red-700" : item.enabled ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}
                      >
                        {item.lastError
                          ? "Needs attention"
                          : item.enabled
                            ? "Enabled"
                            : "Disabled"}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-stone-500">
                      {definition?.label ?? item.kind} ·{" "}
                      {item.scope === "organization"
                        ? "Shared with organization"
                        : "Personal"}
                      {item.address
                        ? ` · ${item.address}`
                        : item.emailAddress
                          ? ` · ${item.emailAddress}`
                          : ""}
                    </p>
                    <div className="mt-2">
                      <CapabilityPills capabilities={capabilities} />
                    </div>
                    {item.lastError ? (
                      <p className="mt-2 text-xs text-red-600">
                        {t("Connection issue: {{error}}", {
                          error: item.lastError,
                        })}
                      </p>
                    ) : item.webhookPath ? (
                      <div className="mt-2 flex max-w-full items-center gap-2 text-xs text-stone-400">
                        <span className="shrink-0">{t("Inbound URL")}</span>
                        <code
                          className="min-w-0 truncate rounded bg-stone-100 px-1.5 py-0.5 dark:bg-white/10"
                          title={webhookUrl}
                        >
                          {webhookUrl}
                        </code>
                        <button
                          type="button"
                          className="button shrink-0 px-2 py-1 text-xs"
                          onClick={() => void copyWebhook(item)}
                          aria-label={`Copy inbound URL for ${item.name}`}
                        >
                          {copiedWebhookId === item.id ? (
                            <>
                              <Check size={13} /> {t("Copied")}
                            </>
                          ) : (
                            <>
                              <Copy size={13} /> {t("Copy")}
                            </>
                          )}
                        </button>
                      </div>
                    ) : (
                      <p className="mt-2 text-xs text-stone-400">
                        {item.lastPolledAt
                          ? `Last checked ${new Date(item.lastPolledAt).toLocaleString()}`
                          : "Not tested yet"}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 sm:ml-auto">
                    {item.kind === "gitea" && (
                      <Link
                        className="button"
                        to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), gitea: item.id })}`}
                      >
                        {t("View pipelines")}
                      </Link>
                    )}
                    <button className="button" onClick={() => void test(item)}>
                      <RefreshCw size={14} /> {t("Test")}
                    </button>
                    <button
                      className="button"
                      onClick={() => void toggle(item)}
                    >
                      {item.enabled ? "Disable" : "Enable"}
                    </button>
                    <button
                      className="button"
                      onClick={() => {
                        setEditing(item);
                        setDraft(draftForIntegration(item));
                        setError("");
                        setMessage("");
                      }}
                    >
                      {/* Keep the edit action close to the connection it changes. */}
                      {t("Edit")}
                    </button>
                    <button
                      className="button text-red-600"
                      aria-label={`Remove ${item.name}`}
                      onClick={() => void remove(item)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </article>
              );
            })}
            {!items.length && (
              <div className="panel flex flex-col items-center px-6 py-16 text-center">
                <span className="grid size-14 place-items-center rounded-2xl bg-lumen-50 text-lumen-700 dark:bg-lumen-950/50 dark:text-lumen-300">
                  <Link2 size={25} />
                </span>
                <h3 className="mt-4 text-lg font-bold">
                  {t("Connect your first service")}
                </h3>
                <p className="mt-2 max-w-md text-sm leading-6 text-stone-500">
                  {t(
                    "Bring messages, calendars, files, CRM records, and analytics\n                  into the workflows you already use.",
                  )}
                </p>
                <button className="button-primary mt-5" onClick={openNew}>
                  <Plus size={16} /> {t("Browse integrations")}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </SettingsPage>
  );
}
